// Company revenue on the pre-search card.
//
// The search carries revenue as the query parser's spelling, "$min–$max" with an en dash and
// either side optional ("–$10M" is under $10M, "$250M–" is over $250M). The card's chips, its
// custom box, a typed reply and memory all go through here so they produce that one spelling,
// which is also what the search cache key compares. backend/services/revenueFilter.js
// (canonicalRevenueRange, describeRevenueRange) holds the same rules for the server side.

export const REVENUE_CHIPS = [
  { label: 'Any',           value: '' },
  { label: 'Under $10M',    value: '–$10M' },
  { label: '$10M to $50M',  value: '$10M–$50M' },
  { label: '$50M to $250M', value: '$50M–$250M' },
  { label: 'Over $250M',    value: '$250M–' },
];

const UNIT_WORDS = { k: 1e3, thousand: 1e3, m: 1e6, mm: 1e6, mil: 1e6, million: 1e6, b: 1e9, bn: 1e9, billion: 1e9 };

function formatAmount(n) {
  const fmt = (v, suffix) => `$${Number.isInteger(v) ? v : Number(v.toFixed(1))}${suffix}`;
  if (n >= 1e9) return fmt(n / 1e9, 'B');
  if (n >= 1e6) return fmt(n / 1e6, 'M');
  if (n >= 1e3) return fmt(n / 1e3, 'K');
  return `$${Math.round(n)}`;
}

function formatRange(min, max) {
  const lo = min > 0 ? formatAmount(min) : '';
  const hi = max < Infinity ? formatAmount(max) : '';
  return lo || hi ? `${lo}–${hi}` : '';
}

function parseAmount(s) {
  const upper = String(s || '').replace(/^[><~+≥≤]+\s*/, '').replace(/\+$/, '').toUpperCase().replace(/[$,\s]/g, '');
  if (!upper) return null;
  const n = parseFloat(upper);
  if (Number.isNaN(n)) return null;
  if (upper.endsWith('B')) return n * 1e9;
  if (upper.endsWith('M')) return n * 1e6;
  if (upper.endsWith('K')) return n * 1e3;
  return n;
}

// Free text or an already canonical value → "$min–$max", or '' when nothing usable is in it.
// A lone figure is a ceiling unless the wording says otherwise ("over", "at least", "+").
export function parseRevenueRange(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';

  if (s.includes('–')) {
    const [a, b] = s.split('–').map(x => x.trim());
    const min = parseAmount(a), max = parseAmount(b);
    if (min == null && max == null) return '';
    return formatRange(min ?? 0, max ?? Infinity);
  }

  const lower = s.toLowerCase().replace(/(\d),(?=\d{3}\b)/g, '$1');
  const tokens = [];
  const re = /(\d+(?:\.\d+)?)\s*(thousand|million|billion|mil|mm|bn|k|m|b)?\b/g;
  let m;
  while ((m = re.exec(lower)) !== null) tokens.push({ n: parseFloat(m[1]), unit: m[2] || '' });
  if (tokens.length === 0) return '';

  // "5 to 20 million": a figure with no unit borrows the next one that has a unit. With no
  // unit anywhere, a small number means millions and a large one is already dollars.
  const lastUnit = [...tokens].reverse().find(t => t.unit)?.unit || '';
  const dollars = tokens.slice(0, 2).map((t, i, arr) => {
    const unit = t.unit || arr.slice(i + 1).find(x => x.unit)?.unit || lastUnit;
    if (unit) return t.n * UNIT_WORDS[unit];
    return t.n >= 1000 ? t.n : t.n * 1e6;
  });

  if (dollars.length >= 2) {
    const [a, b] = dollars[0] <= dollars[1] ? dollars : [dollars[1], dollars[0]];
    return formatRange(a, b);
  }
  const floor = /\b(over|above|more than|at least|greater than|minimum|min|from)\b|>|\+/.test(lower);
  return floor ? formatRange(dollars[0], Infinity) : formatRange(0, dollars[0]);
}

// "–$10M" → "under $10M", "$250M–" → "over $250M", "$10M–$50M" → "$10M to $50M", '' → ''.
export function formatRevenueRange(canonical) {
  const c = parseRevenueRange(canonical);
  if (!c) return '';
  const [lo, hi] = c.split('–');
  if (lo && hi) return `${lo} to ${hi}`;
  return lo ? `over ${lo}` : `under ${hi}`;
}

// A typed reply to the card names a revenue only when it has a money figure: a unit word or a
// K/M/B suffix, or a dollar sign. A bare "25" stays a count or a cap.
export function hasRevenueSignal(text) {
  const t = String(text || '').toLowerCase();
  return /\$\s*\d|\d\s*(?:k|m|mm|b|bn)\b|\d\s*(?:thousand|million|billion)\b|\brevenue\b/.test(t)
    && parseRevenueRange(t.replace(/\brevenue\b/g, '')) !== '';
}

// The revenue in a typed reply that may also answer other rows: "max 2, under 10 million".
// Only the clause carrying the money figure is parsed, so the cap's "2" is never read as $2M.
// Returns the canonical range, '' for "any revenue", or undefined when the reply says nothing
// about revenue.
export function extractRevenueAnswer(text) {
  if (isAnyRevenueAnswer(text)) return '';
  const clauses = String(text || '').split(/[,;]|\band\b|\bbut\b|\bwith\b/i).map(c => c.trim()).filter(Boolean);
  const clause = clauses.find(hasRevenueSignal);
  if (!clause) return undefined;
  return parseRevenueRange(clause.replace(/\brevenue\b|\bin\b/gi, ' ')) || undefined;
}

// "any revenue", "no revenue limit": the user clearing the row by typing.
export function isAnyRevenueAnswer(text) {
  return /\bany\s+revenue\b|\bno\s+revenue\s+(?:limit|cap|filter|minimum|maximum)\b|\brevenue\s*:\s*any\b/i.test(String(text || ''));
}
